import { computed, inject, type InjectionKey } from 'vue'
import type { CreateNewSupportSessionRequest } from '@gat-ship/types'
import {
  countActiveSupportSessions,
  sortSupportSessionsByGrantedToDesc
} from '../model/support-session-utils'
import { useSupportApiCalls } from './useSupportApiCalls'
import { useSupportSessionDataSource } from './useSupportSessionDataSource'

/**
 * Business logic for the granting (normal, non-Gatship) user's side of
 * support sessions: the lists they own (my sessions, history) and the
 * mutations on them (creating a session, ending one). Provided once by
 * `SupportModalProvider` via `supportSessionKey`; views, forms and
 * modals inject the shared instance with `useSupportSessionContext()`.
 * The Gatship-user side lives in `useSupportSessionAssigned`.
 */
export function useSupportSession() {
  const { createNewSupportSessionRequest, endSupportSessionRequest } =
    useSupportApiCalls()

  const mySessionsDataSource = useSupportSessionDataSource(
    'my-support-sessions'
  )
  const historySessionsDataSource = useSupportSessionDataSource('history')

  /** Number of my sessions that have not expired yet. */
  const activeSessionsCount = computed(() =>
    countActiveSupportSessions(mySessionsDataSource.data.value)
  )

  /** My sessions sorted by `grantedTo`, newest first (drives the table). */
  const sortedMySessions = computed(() =>
    sortSupportSessionsByGrantedToDesc(mySessionsDataSource.data.value)
  )

  /**
   * Creates a new support session and assigns it to the chosen support
   * user. The refetched list is the source of truth for the new session
   * — no hand-built optimistic entity to keep in sync.
   */
  async function createSupportSession(
    payload: CreateNewSupportSessionRequest,
    commandId: string
  ) {
    const result = await createNewSupportSessionRequest(payload, commandId)
    await mySessionsDataSource.fetchData({
      forceFetch: true
    })
    return result
  }

  /** Ends an existing support session and drops it from the list. */
  async function endSupportSession(sessionId: string, commandId: string) {
    const result = await endSupportSessionRequest(sessionId, commandId)
    mySessionsDataSource.removeSupportSession(sessionId)
    return result
  }

  const commands = {
    createSupportSession,
    endSupportSession
  }

  return {
    mySessionsDataSource,
    historySessionsDataSource,
    activeSessionsCount,
    sortedMySessions,
    commands
  }
}

export const supportSessionKey = Symbol('support-session') as InjectionKey<
  ReturnType<typeof useSupportSession>
>

/** Inject the shared service instance provided by `SupportModalProvider`. */
export function useSupportSessionContext() {
  return inject(supportSessionKey)
}
